/**
 * Where to start on /admin/attempts.
 *
 * With one student an admin watched every recording. With a cohort of forty
 * that is twenty hours of video per exam, so in practice nobody watches any of
 * it and the proctoring becomes theatre. A flat reverse-chronological list does
 * not tell anyone where two minutes of attention should land; this panel does.
 *
 * Everything shown here is a REASON TO LOOK, never a finding. Nothing on this
 * panel means a student did anything wrong, and the copy is written so that it
 * cannot be read that way. That is also why there is no bare score: the
 * priority is printed as the sum of the weights beside it, each weight next to
 * the sentence that earned it, so an admin can disagree with the order on the
 * evidence instead of having to trust a number.
 */
import { Link } from "react-router-dom";
import { ChevronRight } from "lucide-react";
import type { ReviewCandidate, ReviewTriageResponse } from "@exam/shared";
import { REVIEW_REASON_LABEL } from "@exam/shared";
import { Badge, Card, CardHeader } from "@/components/ui";
import { EmptyState, ErrorNote, Loading } from "./Shell";
import { CoverageBar } from "./CoverageBar";
import { formatDateTime, formatMarks } from "../lib/format";
import type { AsyncState } from "../lib/useAsync";

export function ReviewTriage({
  state,
  examLabel,
}: {
  /**
   * Fetched by the page rather than here, because the attempts table needs the
   * same response for its coverage column and asking the Worker for the same
   * ranking twice would be waste with a chance of disagreeing with itself.
   */
  state: AsyncState<ReviewTriageResponse>;
  /** The exam currently filtered to, so the header says what was considered. */
  examLabel: string | null;
}) {
  const data = state.data;
  const scope = examLabel ? ` in ${examLabel}` : "";

  return (
    <Card>
      <CardHeader
        title="Where to start"
        description={
          data
            ? `${data.flagged} of ${data.considered} graded attempt${
                data.considered === 1 ? "" : "s"
              }${scope} worth a look. Each is a reason to look, not a finding — the order is the sum of the weights shown.`
            : "Ranking the graded attempts by how much a human ought to check them."
        }
      />

      {state.loading ? (
        <Loading label="Ranking attempts…" />
      ) : state.error ? (
        <div className="p-4">
          <ErrorNote error={state.error} />
        </div>
      ) : !data || data.flagged === 0 ? (
        // Nothing flagged is a real result, not a blank screen: every graded
        // attempt was actually checked against every signal and came back
        // clean. Saying so plainly is what makes the panel worth trusting the
        // day it does flag something.
        <EmptyState>
          {data && data.considered > 0
            ? `All ${data.considered} graded attempt${
                data.considered === 1 ? "" : "s"
              }${scope} were checked — recordings covered the exams, no proctor events, nothing unusual in the timings. Nothing stood out.`
            : "No graded attempts to rank yet."}
        </EmptyState>
      ) : (
        <ul className="divide-y divide-border">
          {/* The API has already ranked these; re-sorting here would silently
              disagree with the weights printed on every row. */}
          {data.candidates.map((candidate) => (
            <CandidateRow key={candidate.attempt_id} candidate={candidate} />
          ))}
        </ul>
      )}
    </Card>
  );
}

function CandidateRow({ candidate }: { candidate: ReviewCandidate }) {
  // `score` is null on an attempt whose grading never completed. Coercing it
  // to 0 prints "0%" — a measured result — for something that was never
  // measured, in a queue where a zero pulls the eye.
  const ratio =
    candidate.score !== null &&
    candidate.max_score !== null &&
    candidate.max_score > 0
      ? candidate.score / candidate.max_score
      : null;

  return (
    <li className="px-4 py-3 hover:bg-surface-sunken">
      <div className="flex items-start gap-4">
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
            <Link
              to={`/admin/attempts/${candidate.attempt_id}`}
              className="text-sm font-medium text-ink hover:underline"
            >
              {candidate.student_name}
            </Link>
            <span className="truncate text-xs text-ink-muted">
              {candidate.exam_title}
            </span>
            {/* Only worth saying when there has been more than one sitting;
                on a single attempt "attempt 1" is noise. */}
            {candidate.attempt_no > 1 ? (
              <Badge tone="neutral">Attempt {candidate.attempt_no}</Badge>
            ) : null}
          </div>

          <div className="mt-0.5 flex flex-wrap items-center gap-x-3 text-2xs text-ink-faint">
            <span>
              Submitted <time>{formatDateTime(candidate.submitted_at)}</time>
            </span>
            <span className="tabular">
              {formatMarks(candidate.score)} / {formatMarks(candidate.max_score)}
              {ratio !== null ? (
                <span className="ml-1">({Math.round(ratio * 100)}%)</span>
              ) : null}
            </span>
          </div>

          <ul className="mt-2 space-y-1">
            {candidate.reasons.map((reason, index) => (
              <li
                // Reasons carry no id and a kind can legitimately appear once
                // only, so the kind plus its position is stable for this list.
                key={`${reason.kind}-${index}`}
                className="flex items-baseline gap-2 text-xs"
              >
                <span className="tabular w-8 shrink-0 text-right text-2xs font-medium text-ink-muted">
                  +{reason.weight}
                </span>
                <span className="min-w-0">
                  <span className="text-ink">
                    {REVIEW_REASON_LABEL[reason.kind]}
                  </span>{" "}
                  <span className="text-ink-muted">— {reason.detail}</span>
                </span>
              </li>
            ))}
          </ul>
        </div>

        <div className="flex shrink-0 items-start gap-4">
          <div>
            <p className="text-2xs text-ink-faint">Recorded</p>
            <div className="mt-1">
              <CoverageBar coverage={candidate.coverage} />
            </div>
          </div>

          <div className="w-16 text-right">
            <p className="text-2xs text-ink-faint">Priority</p>
            <p className="tabular mt-0.5 text-sm font-semibold text-ink">
              {candidate.priority}
            </p>
            <p className="text-2xs text-ink-faint">sum of above</p>
          </div>

          <Link
            to={`/admin/attempts/${candidate.attempt_id}`}
            className="mt-1 inline-flex text-ink-faint hover:text-ink"
            aria-label={`Open ${candidate.student_name}'s attempt at ${candidate.exam_title}`}
          >
            <ChevronRight className="size-4" />
          </Link>
        </div>
      </div>
    </li>
  );
}
